import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAdminDelete, DeleteBar, ConfirmDelete, SelectBox, SelectAll, RowDelete } from "@/components/AdminDelete";

const FILTERS = [["all", "All"], ["bug", "Bug"], ["idea", "Idea"], ["praise", "Praise"], ["other", "Other"]];

const AlertEmail = () => {
  const [saved, setSaved] = useState(undefined);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/alert-email").then((r) => { setSaved(r.data.alert_email); setValue(r.data.alert_email || ""); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/alert-email", { email: value });
      setSaved(data.alert_email);
      toast.success(data.alert_email ? "Alert email saved." : "Alert email cleared.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} noValidate data-testid="admin-alert-email-form" className="glass hairline-gold mb-6 rounded-2xl border p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label htmlFor="alert-email" className="eyebrow">Alert email</label>
        {saved === null && <span data-testid="admin-alert-email-unset" className="rounded-full border border-[#E9C98B]/40 bg-[#E9C98B]/10 px-3 py-1 text-xs text-[#F3DDAE]">Set your alert email</span>}
        {saved && <span data-testid="admin-alert-email-current" className="break-all text-xs text-muted-foreground">Bug reports go to <span className="text-foreground">{saved}</span></span>}
      </div>
      <p className="mt-2 text-sm text-muted-foreground">Every Bug-type feedback is emailed here the moment it arrives.</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input id="alert-email" type="email" value={value} onChange={(e) => setValue(e.target.value)} placeholder="founder@yourdomain.com"
          data-testid="admin-alert-email-input" className="h-11 rounded-xl sm:flex-1" />
        <Button type="submit" disabled={busy} data-testid="admin-alert-email-save" className="btn-aurora h-11 rounded-xl px-6">{busy ? "Saving…" : "Save"}</Button>
      </div>
    </form>
  );
};

export const AdminFeedback = ({ onUnread }) => {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/admin/feedback", { params: filter === "all" ? {} : { type: filter } })
    .then((r) => { setItems(r.data.feedback); onUnread(r.data.unread); }).catch((e) => toast.error(errMsg(e))), [filter, onUnread]);
  useEffect(() => { load(); }, [load]);
  const mark = (id, read) => api.patch(`/admin/feedback/${id}`, { read }).then(load).catch((e) => toast.error(errMsg(e)));
  const del = useAdminDelete("feedback", load);

  return (
    <div data-testid="admin-feedback">
      <AlertEmail />
      <div className="flex flex-wrap items-center gap-1.5">
        {items?.length > 0 && <label className="mr-2 inline-flex min-h-[44px] items-center gap-2 text-xs text-muted-foreground"><SelectAll d={del} ids={items.map((f) => f.id)} />Select all</label>}
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`admin-feedback-filter-${k}`}
            className={`min-h-[44px] rounded-full border px-4 text-xs transition-colors ${filter === k ? "border-[#E9C98B]/60 bg-[#E9C98B]/10 text-[#F3DDAE]" : "border-white/10 hover:bg-white/[0.04]"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-4"><DeleteBar d={del} /></div>
      <ConfirmDelete d={del} />
      <ul className="mt-2 space-y-3">
        {items?.map((f) => (
          <li key={f.id} data-testid={`admin-feedback-item-${f.id}`} data-read={f.read}
            className={`glass rounded-2xl border p-5 ${f.read ? "opacity-70" : "hairline-gold"}`}>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <SelectBox d={del} id={f.id} />
              <span className="rounded-full border border-[#E9C98B]/35 px-2.5 py-0.5 uppercase tracking-[0.16em] text-[#F3DDAE]" data-testid="admin-feedback-type">{f.type}</span>
              {!f.read && <span className="h-1.5 w-1.5 rounded-full bg-[#E9C98B] shadow-[0_0_8px_#E9C98B]" />}
              <span className="text-foreground" data-testid="admin-feedback-email">{f.email}</span>
              <span className="font-mono text-muted-foreground">{format(new Date(f.created_at), "MMM d, yyyy HH:mm")}</span>
            </div>
            <p className="mt-3 whitespace-pre-line text-[15px] [overflow-wrap:anywhere]">{f.message}</p>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <span className="break-all font-mono text-xs text-muted-foreground" data-testid="admin-feedback-url">{f.page_url}</span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => mark(f.id, !f.read)} data-testid={`admin-feedback-mark-${f.id}`}>
                  {f.read ? "Mark unread" : "Mark as read"}
                </Button>
                <RowDelete d={del} id={f.id} />
              </div>
            </div>
          </li>
        ))}
        {items && !items.length && <li className="text-sm text-muted-foreground">No feedback yet.</li>}
      </ul>
    </div>
  );
};
